import { DateTime } from 'luxon';

/** A pick in progress: check-in and checkout day keys (`yyyy-MM-dd`, listing zone). */
export interface NightsValue {
  start: string | null;
  end: string | null;
}

export interface NightsRules {
  /** Nights (the day each night starts) that cannot be booked. */
  blocked: Set<string>;
  minNights?: number | null;
  maxNights?: number | null;
}

/** Night keys from check-in (inclusive) to checkout (exclusive). */
export function nightKeys(start: string, end: string): string[] {
  const keys: string[] = [];
  const last = DateTime.fromISO(end);
  for (let day = DateTime.fromISO(start); day < last; day = day.plus({ days: 1 })) {
    keys.push(day.toISODate() as string);
  }
  return keys;
}

/**
 * Whether `day` can be the checkout for a stay from `start`: every night in
 * between is free and the length fits. The checkout day itself may be a
 * blocked night, so a stay can end the day another one begins (spec 016).
 */
export function isValidCheckout(start: string, day: string, rules: NightsRules): boolean {
  if (day <= start) return false;
  const nights = nightKeys(start, day);
  if (nights.some(key => rules.blocked.has(key))) return false;
  if (nights.length < (rules.minNights ?? 1)) return false;
  if (rules.maxNights && nights.length > rules.maxNights) return false;
  return true;
}

/**
 * Airbnb's taps: the first tap picks check-in (a free night); the next tap on
 * a valid checkout completes the stay; a tap that cannot be a checkout starts
 * over from that day when it is free. A tap after a complete stay starts over.
 */
export function nextNightsOnTap(value: NightsValue, day: string, rules: NightsRules): NightsValue {
  const freeNight = !rules.blocked.has(day);
  if (!value.start || value.end) {
    return freeNight ? { start: day, end: null } : value;
  }
  if (isValidCheckout(value.start, day, rules)) {
    return { start: value.start, end: day };
  }
  return freeNight && day !== value.start ? { start: day, end: null } : value;
}

/**
 * Whether a day is greyed out. While a checkout is being picked, every day
 * that cannot end this stay is out, except earlier free nights (to restart).
 */
export function isNightsDayDisabled(day: string, value: NightsValue, rules: NightsRules): boolean {
  if (value.start && !value.end && day > value.start) {
    return !isValidCheckout(value.start, day, rules);
  }
  return rules.blocked.has(day);
}
